import { useEffect, useState } from "react";
import Seo from "../components/Seo.tsx";

type ServiceCheck = {
  name: string;
  status: "ok" | "degraded" | "down";
  ms: number;
  detail?: string;
};

type HealthReport = {
  overall: "healthy" | "degraded" | "down";
  timestamp: string;
  checks: ServiceCheck[];
  stats: { totalOrders: number; totalSubscribers: number; totalCreations: number };
};

type OrderRow = {
  productName: string;
  email: string | null;
  amount: number;
  status: string;
  emailSent: number | null;
  createdAt: number;
};

type Dashboard = {
  stats: { totalOrders: number; totalSubscribers: number; totalRevenue: number };
  recentOrders: OrderRow[];
  healthHistory: Array<{ status: string; createdAt: number; details: HealthReport }>;
};

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const ts = (unix: number) =>
  new Date(unix * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const TOKEN_KEY = "beadore:admin:token";

export default function Admin() {
  const [health, setHealth] = useState<HealthReport | null>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsToken, setNeedsToken] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let token = "";
    try {
      token = localStorage.getItem(TOKEN_KEY) ?? "";
    } catch {
      // ignore
    }
    const headers: HeadersInit = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    setLoading(true);
    Promise.all([
      fetch("/api/health/full", { headers }),
      fetch("/api/admin/dashboard", { headers }),
    ])
      .then(async ([hr, dr]) => {
        if (hr.status === 401 || dr.status === 401) {
          setNeedsToken(true);
          return;
        }
        setNeedsToken(false);
        setHealth((await hr.json()) as HealthReport);
        setDashboard((await dr.json()) as Dashboard);
      })
      .catch(() => setNeedsToken(false))
      .finally(() => setLoading(false));
  }, [attempt]);

  if (needsToken) {
    return (
      <div className="mx-auto max-w-md px-6 py-32 text-center">
        <Seo title="Admin" description="BeAdoré admin" path="/admin" noIndex />
        <span className="eyebrow">Admin</span>
        <h1 className="mt-3 font-serif text-3xl text-[var(--color-emerald-deep)]">
          Enter the studio key
        </h1>
        <form
          className="mt-8 flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            try {
              localStorage.setItem(TOKEN_KEY, tokenInput.trim());
            } catch {
              // ignore
            }
            setAttempt((a) => a + 1);
          }}
        >
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Access token"
            className="border border-[var(--color-rule)] bg-[var(--color-warm-white)] px-4 py-3 text-sm"
          />
          <button
            type="submit"
            className="bg-[var(--color-emerald-deep)] px-4 py-3 text-[0.7rem] font-medium uppercase tracking-[0.14em] text-[var(--color-warm-white)]"
          >
            Open Dashboard
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-20 text-center">
        <p className="font-serif text-2xl italic text-[var(--color-stone)]">
          Loading dashboard…
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-20">
      <Seo title="Admin Dashboard" description="BeAdoré system status and order management" path="/admin" noIndex />

      <header className="mb-12">
        <span className="eyebrow">Admin</span>
        <h1 className="mt-3 font-serif text-4xl text-[var(--color-emerald-deep)] md:text-5xl">
          System Dashboard
        </h1>
      </header>

      {/* Overall status */}
      {health && (
        <div
          className={[
            "mb-10 rounded border px-8 py-6",
            health.overall === "healthy"
              ? "border-[var(--color-sage)] bg-[var(--color-sage-light)]/20"
              : health.overall === "degraded"
                ? "border-[var(--color-amber)] bg-[var(--color-amber-light)]/20"
                : "border-[var(--color-oxblood)] bg-[var(--color-oxblood)]/10",
          ].join(" ")}
        >
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-2xl text-[var(--color-emerald-deep)]">
              {health.overall === "healthy"
                ? "All Systems Operational"
                : health.overall === "degraded"
                  ? "Partial Degradation"
                  : "System Down"}
            </h2>
            <span className="text-[0.7rem] uppercase tracking-[0.2em] text-[var(--color-stone)]">
              {new Date(health.timestamp).toLocaleString()}
            </span>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            {health.checks.map((c) => (
              <div
                key={c.name}
                className="flex items-center gap-3 rounded border border-[var(--color-rule)] bg-[var(--color-warm-white)] px-4 py-3"
              >
                <span
                  className={[
                    "h-3 w-3 rounded-full",
                    c.status === "ok"
                      ? "bg-[var(--color-sage)]"
                      : c.status === "degraded"
                        ? "bg-[var(--color-amber)]"
                        : "bg-[var(--color-oxblood)]",
                  ].join(" ")}
                />
                <div>
                  <p className="text-sm font-medium text-[var(--color-emerald-deep)]">
                    {c.name}
                  </p>
                  <p className="text-[0.7rem] text-[var(--color-stone)]">
                    {c.ms}ms{c.detail ? ` · ${c.detail}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats cards */}
      {dashboard && (
        <div className="mb-10 grid gap-6 sm:grid-cols-3">
          <StatCard
            label="Total Orders"
            value={String(dashboard.stats.totalOrders)}
          />
          <StatCard
            label="Total Revenue"
            value={fmt(dashboard.stats.totalRevenue)}
          />
          <StatCard
            label="Subscribers"
            value={String(dashboard.stats.totalSubscribers)}
          />
        </div>
      )}

      {/* Recent orders */}
      {dashboard && dashboard.recentOrders.length > 0 && (
        <section className="mb-10">
          <h2 className="mb-5 font-serif text-2xl text-[var(--color-emerald-deep)]">
            Recent Orders
          </h2>
          <div className="overflow-x-auto rounded border border-[var(--color-rule)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-rule)] bg-[var(--color-cream)]/50">
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[var(--color-amber)]">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[var(--color-amber)]">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-right text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[var(--color-amber)]">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-center text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[var(--color-amber)]">
                    Email
                  </th>
                  <th className="px-4 py-3 text-right text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[var(--color-amber)]">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recentOrders.map((o, i) => (
                  <tr
                    key={i}
                    className="border-b border-[var(--color-rule)]/50 last:border-b-0"
                  >
                    <td className="px-4 py-3 font-serif text-[var(--color-emerald-deep)]">
                      {o.productName}
                    </td>
                    <td className="px-4 py-3 text-[var(--color-stone)]">
                      {o.email ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-serif text-[var(--color-emerald-deep)]">
                      {fmt(o.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {o.emailSent ? (
                        <span className="text-[var(--color-sage)]">Sent</span>
                      ) : (
                        <span className="text-[var(--color-oxblood)]">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-[0.75rem] text-[var(--color-stone)]">
                      {ts(o.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Health history */}
      {dashboard && dashboard.healthHistory.length > 0 && (
        <section>
          <h2 className="mb-5 font-serif text-2xl text-[var(--color-emerald-deep)]">
            Health History (24h)
          </h2>
          <div className="flex flex-wrap gap-1">
            {dashboard.healthHistory.map((h, i) => (
              <div
                key={i}
                title={`${h.status} — ${ts(h.createdAt)}`}
                className={[
                  "h-6 w-2 rounded-sm",
                  h.status === "healthy"
                    ? "bg-[var(--color-sage)]"
                    : h.status === "degraded"
                      ? "bg-[var(--color-amber)]"
                      : "bg-[var(--color-oxblood)]",
                ].join(" ")}
              />
            ))}
          </div>
          <p className="mt-2 text-[0.65rem] text-[var(--color-stone)]">
            Each bar = 5-minute check · green = healthy · amber = degraded · red = down
          </p>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-[var(--color-rule)] bg-[var(--color-warm-white)] px-6 py-5">
      <p className="text-[0.65rem] font-medium uppercase tracking-[0.22em] text-[var(--color-amber)]">
        {label}
      </p>
      <p className="mt-2 font-serif text-3xl text-[var(--color-emerald-deep)]">
        {value}
      </p>
    </div>
  );
}
